/**
 * The pieces of the help centre that belong to the app shell (ADR 0046) — kept small on purpose:
 * the articles, the search, the coach-marks and their texts are separate chunks that load on use.
 *
 *   <HelpButton>   "?" in the top bar: opens the article for the page on screen
 *   <HelpNavLink>  "ศูนย์ช่วยเหลือ" in the menu, with the "มีอะไรใหม่" dot
 *   <HelpHost>     once per tab: shows a page's coach-marks the first time the person opens it, and
 *                  ticks the checklist steps the server cannot see (a page was opened, the app runs
 *                  installed)
 */
import { lazy, Suspense, useEffect, useState } from 'react';
import CircleHelp from 'lucide-react/dist/esm/icons/circle-question-mark.mjs';
import { href, useLocation } from '../lib/router.js';
import { useHelpState } from './state.js';

const CoachMarks = lazy(() => import('./CoachMarks.js'));

/** Pages with coach-marks (help/coach.ts has the marks themselves). */
export function coachPage(path: string): string | null {
  if (/^\/d\/[^/]+$/.test(path)) return 'editor';
  if (path === '/files') return 'files';
  if (path === '/tor') return 'tor';
  if (path === '/pdf') return 'pdf';
  if (path === '/photo') return 'photo';
  return null;
}

/** Checklist steps ticked by opening a page: the same pairs as help/onboarding.ts `VISIT_STEPS` (a test keeps them equal). */
export const VISIT_TICKS: Record<string, string> = { '/photo': 'visit-photo', '/reviews': 'visit-reviews', '/help/guide': 'visit-guide' };

const installed = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
};

export function HelpHost() {
  const loc = useLocation();
  const { state, patch } = useHelpState();
  const page = coachPage(loc.path);
  const onboarding = state?.onboarding;
  const off = !onboarding || onboarding.hidden;
  /** The address whose coach-marks are on screen: they stay until the person leaves the page. */
  const [coaching, setCoaching] = useState<string | null>(null);
  const seen = !!page && !!state?.coachSeen.includes(page);

  useEffect(() => {
    if (page && !off && !seen) setCoaching(loc.path);
  }, [page, off, seen, loc.path]);

  useEffect(() => {
    if (off || onboarding.dismissed) return;
    const step = VISIT_TICKS[loc.path];
    if (step && !onboarding.stepsDone.includes(step)) patch({ stepDone: step });
    else if (!onboarding.stepsDone.includes('install-app') && installed()) patch({ stepDone: 'install-app' });
  }, [off, onboarding, loc.path, patch]);

  if (!page || off || coaching !== loc.path) return null;
  return (
    <Suspense fallback={null}>
      <CoachMarks key={loc.path} page={page} onShown={() => patch({ coachSeen: page })} />
    </Suspense>
  );
}

export function HelpButton({ current }: { current: string }) {
  const { whatsNewUnread } = useHelpState();
  const to = current.startsWith('/help') ? '/help' : `/help?for=${encodeURIComponent(current)}`;
  return (
    <a className="xc-icon-btn xc-help-btn" href={href(to)} aria-label={`ความช่วยเหลือสำหรับหน้านี้${whatsNewUnread ? ' — มีอะไรใหม่ที่ยังไม่ได้อ่าน' : ''}`} title="ความช่วยเหลือสำหรับหน้านี้">
      <CircleHelp aria-hidden="true" size={20} />
      {whatsNewUnread && <span className="xc-help-dot" aria-hidden="true" />}
    </a>
  );
}

export function HelpNavLink({ current, size = 18, onClick }: { current: string; size?: number; onClick?: () => void }) {
  const { whatsNewUnread } = useHelpState();
  const on = current === '/help';
  return (
    <a href={href('/help')} className={on ? 'is-on' : ''} aria-current={on ? 'page' : undefined} onClick={onClick}>
      <CircleHelp aria-hidden="true" size={size} />
      <span>ศูนย์ช่วยเหลือ</span>
      {whatsNewUnread && <span className="xc-help-dot is-inline" role="img" aria-label="มีอะไรใหม่" />}
    </a>
  );
}
